import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Expand } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
	"/_auth/app/_app/help/user-guide/grass-production",
)({ component: RouteComponent });

function RouteComponent() {
	const { t, i18n } = useTranslation();
	const permissionsTitleId = useId();
	const locale = i18n.language === "en_US" ? "en-US" : "pt-BR";
	const sections = [
		{
			id: "list",
			title: t("help.paddockGuide.listTitle"),
			intro: t("help.paddockGuide.listIntro"),
			steps: [
				t("help.paddockGuide.listStep1"),
				t("help.paddockGuide.listStep2"),
				t("help.paddockGuide.listStep3"),
				t("help.paddockGuide.listStep4"),
			],
			note: t("help.paddockGuide.listNote"),
			caption: t("help.paddockGuide.listCaption"),
			width: 1200,
			height: 756,
		},
		{
			id: "create",
			title: t("help.paddockGuide.createTitle"),
			intro: t("help.paddockGuide.createIntro"),
			steps: [
				t("help.paddockGuide.createStep1"),
				t("help.paddockGuide.createStep2"),
				t("help.paddockGuide.createStep3"),
				t("help.paddockGuide.createStep4"),
			],
			note: t("help.paddockGuide.createNote"),
			caption: t("help.paddockGuide.createCaption"),
			width: 1200,
			height: 986,
		},
		{
			id: "edit",
			title: t("help.paddockGuide.editTitle"),
			intro: t("help.paddockGuide.editIntro"),
			steps: [
				t("help.paddockGuide.editStep1"),
				t("help.paddockGuide.editStep2"),
				t("help.paddockGuide.editStep3"),
				t("help.paddockGuide.editStep4"),
			],
			note: t("help.paddockGuide.editNote"),
			caption: t("help.paddockGuide.editCaption"),
			width: 1200,
			height: 986,
		},
		{
			id: "records",
			title: t("help.paddockGuide.recordsTitle"),
			intro: t("help.paddockGuide.recordsIntro"),
			steps: [
				t("help.paddockGuide.recordsStep1"),
				t("help.paddockGuide.recordsStep2"),
				t("help.paddockGuide.recordsStep3"),
				t("help.paddockGuide.recordsStep4"),
			],
			note: t("help.paddockGuide.recordsNote"),
			caption: t("help.paddockGuide.recordsCaption"),
			width: 1200,
			height: 986,
		},
	];

	return (
		<article className="mx-auto max-w-4xl space-y-8 p-4 text-base-content sm:p-8">
			<Link
				to="/app/help/user-guide"
				className="inline-flex items-center gap-2 text-sm text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
			>
				<ArrowLeft className="size-4" aria-hidden="true" />
				{t("help.userGuide")}
			</Link>
			<header className="max-w-prose space-y-4">
				<h1 className="text-3xl font-bold sm:text-4xl">
					{t("help.guideGrassProductionTitle")}
				</h1>
				<p className="text-base leading-relaxed">
					{t("help.paddockGuide.intro")}
				</p>
			</header>
			<nav
				aria-label={t("help.paddockGuide.contents")}
				className="border-y border-base-300 py-5"
			>
				<h2 className="mb-3 font-semibold">
					{t("help.paddockGuide.contents")}
				</h2>
				<ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
					{sections.map((section) => (
						<li key={section.id}>
							<a
								href={`#${section.id}`}
								className="text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								{section.title}
							</a>
						</li>
					))}
				</ul>
			</nav>
			<p className="text-sm text-base-content/70">
				{t("help.paddockGuide.screenshotsNote")}
			</p>
			{sections.map((section) => {
				const src = `/help/paddocks/${section.id}-${locale}.webp`;
				return (
					<section
						key={section.id}
						id={section.id}
						aria-labelledby={`${section.id}-title`}
						className="scroll-mt-6 space-y-5 border-b border-base-300 pb-8"
					>
						<div className="max-w-prose space-y-3">
							<h2
								id={`${section.id}-title`}
								className="text-xl font-semibold sm:text-2xl"
							>
								{section.title}
							</h2>
							<p className="leading-relaxed">{section.intro}</p>
							<ol className="list-decimal space-y-3 pl-5 leading-relaxed marker:font-semibold marker:text-primary">
								{section.steps.map((step) => (
									<li key={step} className="pl-1">
										{step}
									</li>
								))}
							</ol>
						</div>
						<figure className="space-y-3">
							<a
								href={src}
								target="_blank"
								rel="noreferrer"
								aria-label={`${t("help.paddockGuide.enlargeImage")}: ${section.title}`}
								className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
							>
								<img
									src={src}
									alt={section.caption}
									width={section.width}
									height={section.height}
									loading="lazy"
									decoding="async"
									className="h-auto w-full rounded-lg border border-base-300"
								/>
							</a>
							<figcaption className="space-y-2 text-sm leading-relaxed text-base-content/70">
								<p>{section.caption}</p>
								<a
									href={src}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
								>
									<Expand className="size-4" aria-hidden="true" />
									{t("help.paddockGuide.enlargeImage")}
								</a>
							</figcaption>
						</figure>
						{section.note && (
							<p className="max-w-prose rounded-lg bg-base-100 p-4 text-sm leading-relaxed">
								{section.note}
							</p>
						)}
					</section>
				);
			})}
			<section
				aria-labelledby={permissionsTitleId}
				className="max-w-prose space-y-3"
			>
				<h2 id={permissionsTitleId} className="text-xl font-semibold">
					{t("help.paddockGuide.permissionsTitle")}
				</h2>
				<p className="leading-relaxed">
					{t("help.paddockGuide.permissionsBody")}
				</p>
			</section>
		</article>
	);
}
